import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Loader2, Plus, Pencil, Trash2, UserPlus, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABELS = { admin: "Yönetici", editor: "Editör", viewer: "Görüntüleyici" };
const ROLE_STYLES = {
  admin: "bg-sky-100 text-sky-700 border-sky-200",
  editor: "bg-blue-100 text-blue-800 border-blue-200",
  viewer: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, pRes] = await Promise.all([
        api.get("/users"),
        api.get("/users/pending"),
      ]);
      setUsers(uRes.data);
      setPending(pRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approveUser = async (id) => {
    try {
      await api.post(`/users/${id}/approve`);
      toast.success("Kullanıcı onaylandı");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const rejectUser = async (id) => {
    try {
      await api.post(`/users/${id}/reject`);
      toast.success("Kullanıcı reddedildi");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/users/${deleteId}`);
      toast.success("Kullanıcı silindi");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-sky-500 mb-2">Yönetim</div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight font-display">Kullanıcılar</h1>
          <p className="text-slate-600 mt-2">Ekip üyelerini yönetin ve rol atayın.</p>
        </div>
        <Button
          className="bg-sky-500 hover:bg-sky-600 text-white h-11 px-6 font-bold"
          onClick={() => setOpenCreate(true)}
          data-testid="create-user-button"
        >
          <UserPlus size={16} className="mr-2" /> Yeni Kullanıcı
        </Button>
      </div>

      {pending.length > 0 && (
        <div className="site-card p-6 rounded-lg border-2 border-amber-300 bg-amber-50/40" data-testid="pending-users">
          <h2 className="text-lg font-black text-amber-900 uppercase tracking-tight font-display mb-3 flex items-center gap-2">
            <Clock size={18} /> Bekleyen Onaylar ({pending.length})
          </h2>
          <div className="space-y-2">
            {pending.map((u) => (
              <div key={u.id} className="flex items-center justify-between bg-white border border-amber-200 rounded-md p-3" data-testid={`pending-row-${u.id}`}>
                <div>
                  <div className="font-bold text-slate-900">{u.name}</div>
                  <div className="text-xs text-slate-600">{u.email}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white font-bold" onClick={() => approveUser(u.id)} data-testid={`approve-${u.id}`}>
                    <CheckCircle2 size={14} className="mr-1" /> Onayla
                  </Button>
                  <Button size="sm" variant="outline" className="border-2 border-red-300 text-red-700 hover:bg-red-50 font-bold" onClick={() => rejectUser(u.id)} data-testid={`reject-${u.id}`}>
                    <XCircle size={14} className="mr-1" /> Reddet
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="site-card rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="animate-spin text-sky-500" size={32} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-xs uppercase tracking-widest text-slate-500">
                  <th className="text-left py-3 px-4 font-bold">Ad Soyad</th>
                  <th className="text-left py-3 px-4 font-bold">Email</th>
                  <th className="text-left py-3 px-4 font-bold">Rol</th>
                  <th className="text-right py-3 px-4 font-bold">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                    <td className="py-3 px-4 font-semibold text-slate-900">{u.name}</td>
                    <td className="py-3 px-4 text-slate-700">{u.email}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-block px-2.5 py-1 text-xs font-bold uppercase tracking-wider border rounded ${ROLE_STYLES[u.role]}`}>
                        {ROLE_LABELS[u.role]}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(u)} data-testid={`edit-user-${u.id}`}>
                          <Pencil size={14} />
                        </Button>
                        {u.id !== me?.id && (
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteId(u.id)} data-testid={`delete-user-${u.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CreateUserDialog open={openCreate} onClose={() => setOpenCreate(false)} onSaved={() => { setOpenCreate(false); load(); }} />
      <EditUserDialog user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kullanıcıyı sil?</AlertDialogTitle>
            <AlertDialogDescription>Bu kullanıcı kalıcı olarak silinecek.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={onDelete} data-testid="confirm-delete-user">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateUserDialog({ open, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("viewer");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(""); setEmail(""); setPassword(""); setRole("viewer");
    }
  }, [open]);

  const submit = async () => {
    if (!name || !email || !password) {
      toast.error("Tüm alanlar zorunlu");
      return;
    }
    setSaving(true);
    try {
      await api.post("/users", { name, email, password, role });
      toast.success("Kullanıcı oluşturuldu");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle>Yeni Kullanıcı</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Ad Soyad</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="border-2 border-slate-300 mt-1" data-testid="create-user-name" /></div>
          <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="border-2 border-slate-300 mt-1" data-testid="create-user-email" /></div>
          <div><Label>Şifre</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="border-2 border-slate-300 mt-1" data-testid="create-user-password" /></div>
          <div>
            <Label>Rol</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="border-2 border-slate-300 mt-1" data-testid="create-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Yönetici</SelectItem>
                <SelectItem value="editor">Editör</SelectItem>
                <SelectItem value="viewer">Görüntüleyici</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button className="bg-sky-500 hover:bg-sky-600" onClick={submit} disabled={saving} data-testid="create-user-save">
            {saving ? <Loader2 className="animate-spin" size={16} /> : "Oluştur"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("viewer");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name);
      setRole(user.role);
      setPassword("");
    }
  }, [user]);

  if (!user) return null;

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { name, role };
      if (password) payload.password = password;
      await api.put(`/users/${user.id}`, payload);
      toast.success("Kullanıcı güncellendi");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle>Kullanıcı Düzenle</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Ad Soyad</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="border-2 border-slate-300 mt-1" data-testid="edit-user-name" /></div>
          <div><Label>Email</Label><Input value={user.email} disabled className="border-2 border-slate-200 mt-1 bg-slate-50" /></div>
          <div>
            <Label>Rol</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="border-2 border-slate-300 mt-1" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Yönetici</SelectItem>
                <SelectItem value="editor">Editör</SelectItem>
                <SelectItem value="viewer">Görüntüleyici</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Yeni Şifre (opsiyonel)</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Boş bırakılırsa değişmez" className="border-2 border-slate-300 mt-1" data-testid="edit-user-password" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button className="bg-sky-500 hover:bg-sky-600" onClick={submit} disabled={saving} data-testid="edit-user-save">
            {saving ? <Loader2 className="animate-spin" size={16} /> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
